import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Paperclip, Upload } from "lucide-react";
import { api, download, errMsg } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.xml,.p7m,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip";
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const FileRow = ({ f, canDelete, onDelete, invoiceBase, xmlEditable, onToggleXml }) => (
  <li data-testid={`file-row-${f.id}`} className="flex items-center gap-3 py-3">
    <FileText className="h-5 w-5 shrink-0 text-slate-400" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium text-slate-800">{f.original_filename}</div>
      <div className="text-xs text-slate-500">
        {size(f.size)} · {f.uploader_name}{f.uploader_role === "studio" ? " (studio)" : ""} · {new Date(f.created_at).toLocaleDateString("it-IT")}
        {f.invoice_numero && (invoiceBase ? <> · <Link className="text-sky-700 underline" to={`${invoiceBase}/${f.invoice_id}`}>fattura n. {f.invoice_numero}</Link></> : <> · fattura n. {f.invoice_numero}</>)}
      </div>
      {f.nota && <div className="text-xs italic text-slate-500">{f.nota}</div>}
    </div>
    {xmlEditable && f.content_type === "application/pdf" ? (
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <Switch data-testid={`file-in-xml-${f.id}`} checked={!!f.in_xml} onCheckedChange={(v) => onToggleXml(f.id, v)} />
        Nella fattura elettronica
      </label>
    ) : f.in_xml ? (
      <span data-testid={`file-in-xml-badge-${f.id}`} className="rounded bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">Nell'XML</span>
    ) : null}
    <Button variant="ghost" size="icon" data-testid={`file-download-${f.id}`} onClick={() => download(`/files/${f.id}/download`).catch((e) => toast.error(e.message))}>
      <Download className="h-4 w-4" />
    </Button>
    {canDelete && <ConfirmDelete testid={`file-delete-${f.id}`} onConfirm={() => onDelete(f.id)} />}
  </li>
);

export const FilesPanel = ({ invoiceId, companyId, invoiceBase, xmlEditable = false }) => {
  const { user } = useAuth();
  const ref = useRef(null);
  const [data, setData] = useState(null);
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const writable = canWrite(user) && !user.demo;

  const load = useCallback(() => {
    api.get("/files", { params: { invoice_id: invoiceId, company_id: companyId } })
      .then((r) => { setData(r.data); setLoadError(""); })
      .catch((e) => setLoadError(errMsg(e)));
  }, [invoiceId, companyId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name}: troppo grande (max 15 MB)`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      if (invoiceId) fd.append("invoice_id", invoiceId);
      if (companyId) fd.append("company_id", companyId);
      fd.append("nota", nota);
      try { await api.post("/files", fd); ok += 1; } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    if (ok) toast.success(ok === 1 ? "File caricato" : `${ok} file caricati`);
    setNota("");
    setBusy(false);
    if (ref.current) ref.current.value = "";
    load();
  };

  const del = async (id) => {
    try { await api.delete(`/files/${id}`); toast.success("File eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleXml = async (id, v) => {
    try { await api.put(`/files/${id}/xml`, { in_xml: v }); toast.success(v ? "Il PDF sarà inserito nella fattura elettronica" : "PDF tolto dalla fattura elettronica"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const canDelete = (f) => !(invoiceId && f.in_xml && !xmlEditable) && writable && (user.role !== "studio" || f.uploader_role === "studio");

  if (loadError) return <div data-testid="files-error" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{loadError}</div>;

  return (
    <div data-testid="files-panel">
      {writable && (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row">
          <Input data-testid="file-note-input" placeholder="Nota (facoltativa)" value={nota} onChange={(e) => setNota(e.target.value)} className="sm:max-w-xs" />
          <Button data-testid="file-upload-button" variant="outline" disabled={busy} onClick={() => ref.current?.click()}>
            {invoiceId ? <Paperclip className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}{busy ? "Caricamento…" : invoiceId ? "Aggiungi allegato" : "Carica documenti"}
          </Button>
          <input ref={ref} data-testid="file-input" type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files)} />
        </div>
      )}
      {user.demo && <p className="mb-3 text-xs text-slate-500">Nella demo il caricamento dei file è disattivato.</p>}
      {data && data.documents.length === 0 && data.invoice_attachments.length === 0 ? (
        <Empty text={invoiceId ? "Nessun allegato" : "Nessun documento"} />
      ) : (
        <>
          <ul className="divide-y divide-slate-100">{(data?.documents || []).map((f) => <FileRow key={f.id} f={f} canDelete={canDelete(f)} onDelete={del} xmlEditable={xmlEditable && writable} onToggleXml={toggleXml} />)}</ul>
          {data?.invoice_attachments.length > 0 && (
            <>
              <div className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-500">Allegati alle fatture</div>
              <ul className="divide-y divide-slate-100">{data.invoice_attachments.map((f) => <FileRow key={f.id} f={f} canDelete={canDelete(f)} onDelete={del} invoiceBase={invoiceBase} />)}</ul>
            </>
          )}
        </>
      )}
      <p className="mt-4 text-xs text-slate-600">PDF, immagini, XML/P7M, Word, Excel, CSV, TXT, ZIP · massimo 15 MB per file</p>
    </div>
  );
};
